import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Camera, Barcode } from 'lucide-react';
import { BrowserMultiFormatReader } from '@zxing/library';

const ConsultaPrecioFloating = () => {
  const [mostrarPopup, setMostrarPopup] = useState(false);
  const [codigoIngresado, setCodigoIngresado] = useState('');
  const [productosEncontrados, setProductosEncontrados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [scannerActivo, setScannerActivo] = useState(false);
  const [videoStream, setVideoStream] = useState(null);
  const scannerRef = useRef(null);
  const codeReader = useRef(null);

  const cerrarPopup = () => {
    setMostrarPopup(false);
    setCodigoIngresado('');
    setProductosEncontrados([]);
    setBuscando(false);
    setScannerActivo(false);
    
    // Detener scanner si está activo
    if (codeReader.current) {
      codeReader.current.reset();
    }
    
    // Remover popup del DOM si existe
    const popupExistente = document.getElementById('popup-consulta-precio');
    if (popupExistente) {
      document.body.removeChild(popupExistente);
      console.log('🗑️ [CONSULTA_PRECIO] Popup removido del DOM');
    }
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
    }
    
    const existingPopup = document.getElementById('popup-consulta-precio');
    if (existingPopup) {
      existingPopup.remove();
    }
  };

  const buscarProductos = async (codigo) => {
    if (!codigo.trim()) return;

    setBuscando(true);
    try {
      const token = localStorage.getItem('comercio_token');
      const response = await fetch(`https://www.sinfilas.com.ar/sf3/ventas/api/productos_comercio_listar.php?token=${token}`);
      const data = await response.json();

      if (data.success && data.productos) {
        // Filtrar productos que coincidan con el código o descripción
        const productosFiltrados = data.productos.filter(producto => 
          producto.codigo_personalizado?.toLowerCase().includes(codigo.toLowerCase()) ||
          producto.nombre?.toLowerCase().includes(codigo.toLowerCase()) ||
          producto.marca?.toLowerCase().includes(codigo.toLowerCase())
        );
        setProductosEncontrados(productosFiltrados);
        
        // Actualizar resultados en el popup si está abierto
        if (window.actualizarResultadosConsulta) {
          window.actualizarResultadosConsulta(productosFiltrados);
        }
      } else {
        setProductosEncontrados([]);
        if (window.actualizarResultadosConsulta) {
          window.actualizarResultadosConsulta([]);
        }
      }
    } catch (error) {
      console.error('Error buscando productos:', error);
      setProductosEncontrados([]);
      if (window.actualizarResultadosConsulta) {
        window.actualizarResultadosConsulta([]);
      }
    } finally {
      setBuscando(false);
    }
  };

  const iniciarScanner = async () => {
    try {
      console.log('🔍 [SCANNER] Iniciando scanner...');
      
      if (!codeReader.current) {
        codeReader.current = new BrowserMultiFormatReader();
        console.log('🔍 [SCANNER] CodeReader creado');
      }

      const videoElement = document.getElementById('scanner-video');
      if (!videoElement) {
        console.error('❌ [SCANNER] Elemento de video no encontrado');
        return;
      }

      console.log('🔍 [SCANNER] Elemento de video encontrado:', videoElement);
      setScannerActivo(true);
      
      console.log('🔍 [SCANNER] Solicitando acceso a la cámara...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      console.log('🔍 [SCANNER] Stream de video obtenido:', stream);
      setVideoStream(stream);
      videoElement.srcObject = stream;

      // Esperar a que el video esté listo
      videoElement.onloadedmetadata = () => {
        console.log('🔍 [SCANNER] Video metadata cargado');
        videoElement.play().then(() => {
          console.log('🔍 [SCANNER] Video iniciado correctamente');
          
          // Actualizar texto de estado
          const statusText = document.getElementById('scanner-status-text');
          if (statusText) {
            statusText.textContent = '📷 Apunta el código de barras aquí';
            statusText.style.color = '#10b981';
            statusText.style.backgroundColor = 'rgba(16, 185, 129, 0.1)';
          }
        }).catch(err => {
          console.error('❌ [SCANNER] Error reproduciendo video:', err);
        });
      };

      // Configurar el scanner
      console.log('🔍 [SCANNER] Configurando decodificador...');
      codeReader.current.decodeFromVideoDevice(undefined, videoElement, (result, err) => {
        if (result) {
          const codigo = result.getText();
          console.log('🔍 [SCANNER] Código detectado:', codigo);
          
          // Buscar productos con el código detectado
          buscarProductos(codigo);
          
          // Detener scanner después de detectar
          detenerScanner();
        }
        // Solo mostrar errores importantes, no los de "no se encontró código"
        if (err && !(err instanceof Error && err.name === 'NotFoundException') && !err.message.includes('No MultiFormat Readers were able to detect')) {
          console.error('❌ [SCANNER] Error del scanner:', err);
        }
      });

    } catch (error) {
      console.error('❌ [SCANNER] Error iniciando scanner:', error);
      setScannerActivo(false);
      
      // Mostrar mensaje de error en el popup
      const resultadosDiv = document.getElementById('resultados-consulta');
      if (resultadosDiv) {
        resultadosDiv.innerHTML = `
          <div style="color: #ef4444; text-align: center; padding: 20px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px;">
            <strong>Error de Cámara:</strong><br>
            No se pudo acceder a la cámara. Verifica los permisos.
          </div>
        `;
      }
    }
  };

  const detenerScanner = () => {
    if (codeReader.current) {
      codeReader.current.reset();
    }
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      setVideoStream(null);
    }
    setScannerActivo(false);
  };

  const mostrarPopupDirecto = () => {
    console.log('🔍 [CONSULTA_PRECIO] Botón clickeado - Creando popup de consulta');
    
    // Remover popup existente si existe
    const existingPopup = document.getElementById('popup-consulta-precio');
    if (existingPopup) {
      existingPopup.remove();
    }

    // Crear popup con estilos más simples y directos
    const popup = document.createElement('div');
    popup.id = 'popup-consulta-precio';
    
    // Aplicar estilos directamente al elemento
    popup.style.position = 'fixed';
    popup.style.top = '0';
    popup.style.left = '0';
    popup.style.width = '100vw';
    popup.style.height = '100vh';
    popup.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    popup.style.zIndex = '9999999';
    popup.style.display = 'flex';
    popup.style.alignItems = 'center';
    popup.style.justifyContent = 'center';
    popup.style.fontFamily = 'Arial, sans-serif';

    // Contenedor principal del modal
    const modal = document.createElement('div');
    modal.style.backgroundColor = 'white';
    modal.style.borderRadius = '8px';
    modal.style.width = '500px';
    modal.style.maxHeight = '80vh';
    modal.style.overflow = 'hidden';
    modal.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.2)';

    // Header del modal
    const header = document.createElement('div');
    header.style.backgroundColor = '#2563eb';
    header.style.color = 'white';
    header.style.padding = '15px 20px';
    header.style.display = 'flex';
    header.style.alignItems = 'center';
    header.style.justifyContent = 'space-between';

    const titulo = document.createElement('h2');
    titulo.textContent = 'Consulta de Precios';
    titulo.style.margin = '0';
    titulo.style.fontSize = '18px';
    titulo.style.fontWeight = '600';

    const botonCerrar = document.createElement('button');
    botonCerrar.innerHTML = '×';
    botonCerrar.style.background = 'none';
    botonCerrar.style.border = 'none';
    botonCerrar.style.color = 'white';
    botonCerrar.style.fontSize = '24px';
    botonCerrar.style.cursor = 'pointer';
    botonCerrar.style.padding = '0';
    botonCerrar.style.width = '30px';
    botonCerrar.style.height = '30px';
    botonCerrar.style.display = 'flex';
    botonCerrar.style.alignItems = 'center';
    botonCerrar.style.justifyContent = 'center';

    header.appendChild(titulo);
    header.appendChild(botonCerrar);

    // Contenido del modal
    const contenido = document.createElement('div');
    contenido.style.padding = '20px';

    // Sección de búsqueda
    const seccionBusqueda = document.createElement('div');
    seccionBusqueda.style.marginBottom = '20px';

    const labelBusqueda = document.createElement('div');
    labelBusqueda.style.display = 'flex';
    labelBusqueda.style.alignItems = 'center';
    labelBusqueda.style.marginBottom = '10px';
    labelBusqueda.style.fontWeight = '500';
    labelBusqueda.style.color = '#374151';

    const iconoBusqueda = document.createElement('span');
    iconoBusqueda.innerHTML = '🔍';
    iconoBusqueda.style.marginRight = '8px';

    const textoBusqueda = document.createElement('span');
    textoBusqueda.textContent = 'Buscar Producto';

    labelBusqueda.appendChild(iconoBusqueda);
    labelBusqueda.appendChild(textoBusqueda);

    const contenedorInput = document.createElement('div');
    contenedorInput.style.display = 'flex';
    contenedorInput.style.gap = '10px';

    const inputCodigo = document.createElement('input');
    inputCodigo.type = 'text';
    inputCodigo.placeholder = 'Ingresa código, nombre o marca del producto';
    inputCodigo.style.flex = '1';
    inputCodigo.style.padding = '10px';
    inputCodigo.style.border = '1px solid #d1d5db';
    inputCodigo.style.borderRadius = '4px';
    inputCodigo.style.fontSize = '14px';

    const botonBuscar = document.createElement('button');
    botonBuscar.textContent = 'Buscar';
    botonBuscar.style.backgroundColor = '#7c3aed';
    botonBuscar.style.color = 'white';
    botonBuscar.style.border = 'none';
    botonBuscar.style.padding = '10px 20px';
    botonBuscar.style.borderRadius = '4px';
    botonBuscar.style.cursor = 'pointer';
    botonBuscar.style.fontSize = '14px';
    botonBuscar.style.display = 'flex';
    botonBuscar.style.alignItems = 'center';
    botonBuscar.style.gap = '5px';

    contenedorInput.appendChild(inputCodigo);
    contenedorInput.appendChild(botonBuscar);

    seccionBusqueda.appendChild(labelBusqueda);
    seccionBusqueda.appendChild(contenedorInput);


    // Sección de resultados
    const seccionResultados = document.createElement('div');
    seccionResultados.id = 'resultados-consulta';

    const labelResultados = document.createElement('div');
    labelResultados.style.fontWeight = '500';
    labelResultados.style.color = '#374151';
    labelResultados.style.marginBottom = '10px';
    labelResultados.textContent = 'Resultados:';

    seccionResultados.appendChild(labelResultados);

      // Botón de salir
      const seccionBotones = document.createElement('div');
      seccionBotones.style.display = 'flex';
      seccionBotones.style.gap = '10px';
      seccionBotones.style.marginTop = '20px';
      seccionBotones.style.justifyContent = 'center';

      const botonSalir = document.createElement('button');
      botonSalir.textContent = 'Salir';
      botonSalir.style.backgroundColor = '#6b7280';
      botonSalir.style.color = 'white';
      botonSalir.style.border = 'none';
      botonSalir.style.padding = '12px 24px';
      botonSalir.style.borderRadius = '6px';
      botonSalir.style.cursor = 'pointer';
      botonSalir.style.fontSize = '14px';
      botonSalir.style.fontWeight = '500';
      botonSalir.style.display = 'flex';
      botonSalir.style.alignItems = 'center';
      botonSalir.style.justifyContent = 'center';
      botonSalir.style.gap = '5px';

      seccionBotones.appendChild(botonSalir);

      contenido.appendChild(seccionBusqueda);
      contenido.appendChild(seccionResultados);
      contenido.appendChild(seccionBotones);

    modal.appendChild(header);
    modal.appendChild(contenido);
    popup.appendChild(modal);

    document.body.appendChild(popup);
    console.log('🔍 [CONSULTA_PRECIO] Popup agregado al DOM:', popup);

    // FORZAR el display después de agregar al DOM con !important
    setTimeout(() => {
      const popupElement = document.getElementById('popup-consulta-precio');
      if (popupElement) {
        // Aplicar estilos con !important usando setAttribute
        popupElement.setAttribute('style', `
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          background-color: rgba(0, 0, 0, 0.5) !important;
          z-index: 9999999 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-family: Arial, sans-serif !important;
          visibility: visible !important;
          opacity: 1 !important;
        `);

        console.log('🔍 [CONSULTA_PRECIO] Estilos con !important aplicados');
        console.log('🔍 [CONSULTA_PRECIO] Popup visible:', popupElement.offsetParent !== null);
        console.log('🔍 [CONSULTA_PRECIO] Popup display:', window.getComputedStyle(popupElement).display);
      } else {
        console.error('❌ [CONSULTA_PRECIO] Popup NO encontrado en DOM');
      }
    }, 50);

    // Eventos
    botonCerrar.onclick = cerrarPopup;
    popup.onclick = (e) => {
      if (e.target === popup) {
        cerrarPopup();
      }
    };

    // Evento de búsqueda
    const buscarProductosHandler = () => {
      const codigo = inputCodigo.value.trim();
      if (codigo) {
        buscarProductos(codigo);
      }
    };

    botonBuscar.onclick = buscarProductosHandler;
    inputCodigo.onkeypress = (e) => {
      if (e.key === 'Enter') {
        buscarProductosHandler();
      }
    };

      // Evento para salir
      botonSalir.onclick = () => {
        cerrarPopup();
      };

    // Función para mostrar resultados
    const mostrarResultados = (productos) => {
      const resultadosDiv = document.getElementById('resultados-consulta');
      resultadosDiv.innerHTML = '';

      if (productos.length === 0) {
        const mensaje = document.createElement('div');
        mensaje.textContent = 'No se encontraron productos';
        mensaje.style.color = '#6b7280';
        mensaje.style.textAlign = 'center';
        mensaje.style.padding = '20px';
        resultadosDiv.appendChild(mensaje);
        return;
      }

      productos.forEach(producto => {
        const itemProducto = document.createElement('div');
        itemProducto.style.border = '1px solid #e5e7eb';
        itemProducto.style.borderRadius = '4px';
        itemProducto.style.padding = '15px';
        itemProducto.style.marginBottom = '10px';
        itemProducto.style.backgroundColor = '#f9fafb';

        const codigo = document.createElement('div');
        codigo.style.fontWeight = '600';
        codigo.style.color = '#1f2937';
        codigo.style.marginBottom = '5px';
        codigo.textContent = `Código: ${producto.codigo_personalizado || 'N/A'}`;

        const nombre = document.createElement('div');
        nombre.style.color = '#374151';
        nombre.style.marginBottom = '5px';
        nombre.textContent = `Producto: ${producto.nombre || 'N/A'}`;

        const precio = document.createElement('div');
        precio.style.fontWeight = '600';
        precio.style.color = '#059669';
        precio.style.fontSize = '16px';
        precio.textContent = `Precio: $${producto.precio_venta || '0'}`;

        itemProducto.appendChild(codigo);
        itemProducto.appendChild(nombre);
        itemProducto.appendChild(precio);
        resultadosDiv.appendChild(itemProducto);
      });
    };

    // Función para actualizar resultados
    const actualizarResultados = (productos) => {
      mostrarResultados(productos);
    };

    // Exponer función globalmente para uso desde React
    window.actualizarResultadosConsulta = actualizarResultados;


    setMostrarPopup(true);
  };

  console.log('🔍 [CONSULTA_PRECIO] Componente renderizando, mostrarPopup:', mostrarPopup);

  return (
    <>
      <button
        onClick={mostrarPopupDirecto}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: '#2563eb',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          transition: 'all 0.3s ease'
        }}
        title="Consultar Precios"
      >
        <Search size={24} />
      </button>
    </>
  );
};

export default ConsultaPrecioFloating;